import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify'; // Assuming you have react-toastify for notifications
import 'react-toastify/dist/ReactToastify.css';

const LoginPage = () => {
    const [identifier, setIdentifier] = useState(''); // For regular login
    const [password, setPassword] = useState(''); // For regular login
    const [error, setError] = useState(''); // For general login errors
    const [loading, setLoading] = useState(false); // For regular login loading

    // NEW STATES for Forgot Password/OTP Flow
    const [showForgotPassword, setShowForgotPassword] = useState(false); // Controls visibility of OTP section
    const [forgotIdentifier, setForgotIdentifier] = useState(''); // For OTP request: phone number or email
    const [otp, setOtp] = useState(''); // For OTP input
    const [requestId, setRequestId] = useState(''); // To store requestId from backend
    const [otpRequested, setOtpRequested] = useState(false); // True after OTP request is sent successfully
    const [otpRequestLoading, setOtpRequestLoading] = useState(false); // Loading for OTP request
    const [otpVerifyLoading, setOtpVerifyLoading] = useState(false); // Loading for OTP verification
    const [otpError, setOtpError] = useState(''); // Error message for OTP flow

    const navigate = useNavigate();

    // Check if user is already logged in
    useEffect(() => {
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
            navigate('/'); // Redirect to home if already logged in
        }
    }, [navigate]);

    // Handler for regular login submission
    const submitHandler = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ identifier, password }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || 'Login failed. Please check your credentials.');
            }

            localStorage.setItem('userInfo', JSON.stringify(data));
            navigate('/'); // Redirect to home on successful login
            toast.success('Login successful!'); // Show success toast

        } catch (err) {
            setError(err.message);
            toast.error(err.message); // Show error toast
        } finally {
            setLoading(false);
        }
    };

    // Handler for requesting OTP (Forgot Password flow)
    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setOtpError(''); // Clear previous OTP errors
        setOtpRequestLoading(true);

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/request-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ identifier: forgotIdentifier }),
            });

            const data = await res.json();

            if (!res.ok) {
                // Backend returns 500 if sending fails, or other errors
                setOtpError(data.message || 'Failed to request OTP. Please try again.');
                toast.error(data.message || 'Failed to request OTP.');
            } else {
                // Backend returns 200 for success, even if user not found (for security)
                toast.info(data.message); // "OTP sent successfully" or "If a valid account exists..."
                if (data.requestId) { // Only proceed if requestId is present, meaning OTP was actually sent for a found user
                    setRequestId(data.requestId);
                    setOtpRequested(true);
                } else {
                    // If backend says "If a valid account exists..." but doesn't provide requestId
                    setOtpError('Could not send OTP. Please check the identifier.');
                }
            }
        } catch (err) {
            console.error('Error requesting OTP:', err);
            setOtpError('Network error. Could not connect to server.');
            toast.error('Network error. Could not connect to server.');
        } finally {
            setOtpRequestLoading(false);
        }
    };

    // Handler for verifying OTP
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setOtpError(''); // Clear previous OTP errors
        setOtpVerifyLoading(true);

        if (!forgotIdentifier || !otp || !requestId) {
            setOtpError('Please provide identifier, OTP, and try requesting again.');
            setOtpVerifyLoading(false);
            return;
        }

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/verify-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ identifier: forgotIdentifier, otp, requestId }),
            });

            const data = await res.json();

            if (!res.ok) {
                setOtpError(data.message || 'OTP verification failed. Please try again.');
                toast.error(data.message || 'OTP verification failed.');
            } else {
                // OTP verified successfully, log user in
                localStorage.setItem('userInfo', JSON.stringify(data));
                toast.success('OTP verified and logged in! Please reset the password from Profile');
                navigate('/'); // Redirect to home
            }

        } catch (err) {
            console.error('Error verifying OTP:', err);
            setOtpError('Network error. Could not connect to server.');
            toast.error('Network error. Could not connect to server.');
        } finally {
            setOtpVerifyLoading(false);
        }
    };


    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
                <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
                    {showForgotPassword ? 'Forgot Password?' : 'Login'}
                </h2>

                {error && <p className="text-red-500 text-center mb-4">{error}</p>}
                {otpError && <p className="text-red-500 text-center mb-4">{otpError}</p>}


                {!showForgotPassword ? ( // Regular Login Form
                    <form onSubmit={submitHandler}>
                        <div className="mb-4">
                            <label htmlFor="identifier" className="block text-gray-700 text-sm font-bold mb-2">
                                Phone Number or Email
                            </label>
                            <input
                                type="text"
                                id="identifier"
                                className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={identifier}
                                onChange={(e) => setIdentifier(e.target.value)}
                                placeholder="Enter phone or email"
                                required
                            />
                        </div>
                        <div className="mb-6">
                            <label htmlFor="password" className="block text-gray-700 text-sm font-bold mb-2">
                                Password
                            </label>
                            <input
                                type="password"
                                id="password"
                                className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 mb-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                required
                                disabled={loading}
                            />
                        </div>
                        <div className="pt-4">
                            <button
                                type="submit"
                                className={`
                                    py-3 px-6 rounded-lg focus:outline-none focus:ring-4 w-full text-xl transition duration-300 transform hover:scale-105
                                    ${loading
                                        ? 'bg-gray-500 text-white cursor-not-allowed shadow-none'
                                        : 'bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold shadow-lg focus:ring-blue-300'
                                    }
                                `}
                                disabled={loading}
                            >
                                {loading ? 'Logging In...' : 'Login'}
                            </button>
                        </div>
                    </form>
                ) : ( // Forgot Password / OTP Form
                    <form onSubmit={otpRequested ? handleVerifyOtp : handleRequestOtp}>
                        {!otpRequested && (
                            <div className="mb-4">
                                <label htmlFor="forgotIdentifier" className="block text-gray-700 text-sm font-bold mb-2">
                                    Email for OTP
                                </label>
                                <input
                                    type="text"
                                    id="forgotIdentifier"
                                    className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={forgotIdentifier}
                                    onChange={(e) => setForgotIdentifier(e.target.value)}
                                    placeholder="Enter your email "
                                    required
                                    disabled={otpRequestLoading || otpVerifyLoading}
                                />
                            </div>
                        )}

                        {otpRequested && (
                            <div className="mb-4">
                                <label htmlFor="otp" className="block text-gray-700 text-sm font-bold mb-2">
                                    Enter OTP
                                </label>
                                <input
                                    type="text"
                                    id="otp"
                                    className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    placeholder="Enter the 6-digit OTP"
                                    maxLength="6"
                                    required
                                    disabled={otpVerifyLoading}
                                />
                            </div>
                        )}

                        <div className="pt-4">
                            <button
                                type="submit"
                                className={`
                                    py-3 px-6 rounded-lg focus:outline-none focus:ring-4 w-full text-xl transition duration-300 transform hover:scale-105
                                    ${(otpRequestLoading || otpVerifyLoading)
                                        ? 'bg-gray-500 text-white cursor-not-allowed shadow-none'
                                        : 'bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white font-bold shadow-lg focus:ring-green-300'
                                    }
                                `}
                                disabled={otpRequestLoading || otpVerifyLoading}
                            >
                                {otpRequestLoading ? 'Sending OTP...' : otpVerifyLoading ? 'Verifying OTP...' : otpRequested ? 'Verify OTP & Login' : 'Request OTP'}
                            </button>
                        </div>
                    </form>
                )}

                {/* Toggle Links */}
                <p className="text-center mt-6 text-gray-600 text-md">
                    {showForgotPassword ? (
                        <>
                            Remember your password?{' '}
                            <button
                                type="button"
                                onClick={() => setShowForgotPassword(false)}
                                className="text-blue-600 hover:underline font-bold text-lg hover:text-blue-800 transition duration-300"
                            >
                                Back to Login
                            </button>
                        </>
                    ) : (
                        <>
                            Don't have an account?{' '}
                            <Link to="/register" className="text-blue-600 hover:underline font-bold text-lg hover:text-blue-800 transition duration-300">
                                Register here!
                            </Link>
                            <br />
                            <button
                                type="button"
                                onClick={() => {
                                    setShowForgotPassword(true);
                                    setOtpError(''); // Clear errors when switching context
                                    setError('');
                                    setOtpRequested(false); // Reset OTP state
                                    setForgotIdentifier(''); // Clear identifier for new request
                                    setOtp('');
                                    setRequestId('');
                                }}
                                className="text-blue-600 hover:underline font-bold text-md mt-2 hover:text-blue-800 transition duration-300"
                            >
                                Forgot Password?
                            </button>
                        </>
                    )}
                </p>
            </div>
        </div>
    );
};

export default LoginPage;